/**
 * @file bible_language_utils.js
 * @description Utilities for handling Bible book names across different languages
 * Provides proper capitalization and expansion of book names from LSM API responses
 */

import { BIBLE_LANGUAGES } from '../constants';

/**
 * Multi-language Bible book name mappings
 * Maps abbreviations/variations to full proper names for each language
 */

// English abbreviation to full name mapping (from existing lsm_api.js)
const ENGLISH_BOOK_NAMES = {
  'Gen.': 'Genesis',
  'Ex.': 'Exodus',
  'Lev.': 'Leviticus',
  'Num.': 'Numbers',
  'Deut.': 'Deuteronomy',
  'Josh.': 'Joshua',
  'Judg.': 'Judges',
  'Ruth': 'Ruth',
  '1 Sam.': '1 Samuel',
  '2 Sam.': '2 Samuel',
  '1 Kings': '1 Kings',
  '2 Kings': '2 Kings',
  '1 Chron.': '1 Chronicles',
  '2 Chron.': '2 Chronicles',
  'Ezra': 'Ezra',
  'Neh.': 'Nehemiah',
  'Esther': 'Esther',
  'Job': 'Job',
  'Psa.': 'Psalms',
  'Prov.': 'Proverbs',
  'Eccl.': 'Ecclesiastes',
  'S.S.': 'Song of Songs',
  'Isa.': 'Isaiah',
  'Jer.': 'Jeremiah',
  'Lam.': 'Lamentations',
  'Ezek.': 'Ezekiel',
  'Dan.': 'Daniel',
  'Hosea': 'Hosea',
  'Joel': 'Joel',
  'Amos': 'Amos',
  'Obad.': 'Obadiah',
  'Jonah': 'Jonah',
  'Micah': 'Micah',
  'Nahum': 'Nahum',
  'Hab.': 'Habakkuk',
  'Zeph.': 'Zephaniah',
  'Hag.': 'Haggai',
  'Zech.': 'Zechariah',
  'Mal.': 'Malachi',
  'Matt.': 'Matthew',
  'Mark': 'Mark',
  'Luke': 'Luke',
  'John': 'John',
  'Acts': 'Acts',
  'Rom.': 'Romans',
  '1 Cor.': '1 Corinthians',
  '2 Cor.': '2 Corinthians',
  'Gal.': 'Galatians',
  'Eph.': 'Ephesians',
  'Phil.': 'Philippians',
  'Col.': 'Colossians',
  '1 Thes.': '1 Thessalonians',
  '2 Thes.': '2 Thessalonians',
  '1 Tim.': '1 Timothy',
  '2 Tim.': '2 Timothy',
  'Titus': 'Titus',
  'Philem.': 'Philemon',
  'Heb.': 'Hebrews',
  'James': 'James',
  '1 Pet.': '1 Peter',
  '2 Pet.': '2 Peter',
  '1 John': '1 John',
  '2 John': '2 John',
  '3 John': '3 John',
  'Jude': 'Jude',
  'Rev.': 'Revelation'
};

// Spanish abbreviation to full name mapping
const SPANISH_BOOK_NAMES = {
  'Gn.': 'Génesis',
  'Ex.': 'Éxodo',
  'Lv.': 'Levítico',
  'Nm.': 'Números',
  'Dt.': 'Deuteronomio',
  'Jos.': 'Josué',
  'Jue.': 'Jueces',
  'Rt.': 'Rut',
  '1S.': '1 Samuel',
  '2S.': '2 Samuel',
  '1R.': '1 Reyes',
  '2R.': '2 Reyes',
  '1Cr.': '1 Crónicas',
  '2Cr.': '2 Crónicas',
  'Esd.': 'Esdras',
  'Neh.': 'Nehemías',
  'Est.': 'Ester',
  'Job': 'Job',
  'Sal.': 'Salmos',
  'Pr.': 'Proverbios',
  'Ec.': 'Eclesiastés',
  'Cnt.': 'Cantares',
  'Is.': 'Isaías',
  'Jer.': 'Jeremías',
  'Lm.': 'Lamentaciones',
  'Ez.': 'Ezequiel',
  'Dn.': 'Daniel',
  'Os.': 'Oseas',
  'Jl.': 'Joel',
  'Am.': 'Amós',
  'Abd.': 'Abdías',
  'Jon.': 'Jonás',
  'Mi.': 'Miqueas',
  'Nah.': 'Nahúm',
  'Hab.': 'Habacuc',
  'Sof.': 'Sofonías',
  'Hag.': 'Hageo',
  'Zac.': 'Zacarías',
  'Mal.': 'Malaquías',
  'Mt.': 'Mateo',
  'Mr.': 'Marcos',
  'Lc.': 'Lucas',
  'Jn.': 'Juan',
  'Hch.': 'Hechos',
  'Ro.': 'Romanos',
  '1Co.': '1 Corintios',
  '2Co.': '2 Corintios',
  'Gá.': 'Gálatas',
  'Ef.': 'Efesios',
  'Fil.': 'Filipenses',
  'Col.': 'Colosenses',
  '1Ts.': '1 Tesalonicenses',
  '2Ts.': '2 Tesalonicenses',
  '1Ti.': '1 Timoteo',
  '2Ti.': '2 Timoteo',
  'Tit.': 'Tito',
  'Flm.': 'Filemón',
  'He.': 'Hebreos',
  'Stg.': 'Santiago',
  '1P.': '1 Pedro',
  '2P.': '2 Pedro',
  '1Jn.': '1 Juan',
  '2Jn.': '2 Juan',
  '3Jn.': '3 Juan',
  'Jud.': 'Judas',
  'Ap.': 'Apocalipsis'
};

// Simplified Chinese abbreviation to full name mapping
const SIMPLIFIED_CHINESE_BOOK_NAMES = {
  '创': '创世记',
  '出': '出埃及记',
  '利': '利未记',
  '民': '民数记',
  '申': '申命记',
  '书': '约书亚记',
  '士': '士师记',
  '得': '路得记',
  '撒上': '撒母耳记上',
  '撒下': '撒母耳记下',
  '王上': '列王纪上',
  '王下': '列王纪下',
  '代上': '历代志上',
  '代下': '历代志下',
  '拉': '以斯拉记',
  '尼': '尼希米记',
  '斯': '以斯帖记',
  '伯': '约伯记',
  '诗': '诗篇',
  '箴': '箴言',
  '传': '传道书',
  '歌': '雅歌',
  '赛': '以赛亚书',
  '耶': '耶利米书',
  '哀': '耶利米哀歌',
  '结': '以西结书',
  '但': '但以理书',
  '何': '何西阿书',
  '珥': '约珥书',
  '摩': '阿摩司书',
  '俄': '俄巴底亚书',
  '拿': '约拿书',
  '弥': '弥迦书',
  '鸿': '那鸿书',
  '哈': '哈巴谷书',
  '番': '西番雅书',
  '该': '哈该书',
  '亚': '撒迦利亚书',
  '玛': '玛拉基书',
  '太': '马太福音',
  '可': '马可福音',
  '路': '路加福音',
  '约': '约翰福音',
  '徒': '使徒行传',
  '罗': '罗马书',
  '林前': '哥林多前书',
  '林后': '哥林多后书',
  '加': '加拉太书',
  '弗': '以弗所书',
  '腓': '腓立比书',
  '西': '歌罗西书',
  '帖前': '帖撒罗尼迦前书',
  '帖后': '帖撒罗尼迦后书',
  '提前': '提摩太前书',
  '提后': '提摩太后书',
  '多': '提多书',
  '门': '腓利门书',
  '来': '希伯来书',
  '雅': '雅各书',
  '彼前': '彼得前书',
  '彼后': '彼得后书',
  '约一': '约翰一书',
  '约二': '约翰二书',
  '约三': '约翰三书',
  '犹': '犹大书',
  '启': '启示录'
};

// Traditional Chinese abbreviation to full name mapping
const TRADITIONAL_CHINESE_BOOK_NAMES = {
  '創': '創世記',
  '出': '出埃及記',
  '利': '利未記',
  '民': '民數記',
  '申': '申命記',
  '書': '約書亞記',
  '士': '士師記',
  '得': '路得記',
  '撒上': '撒母耳記上',
  '撒下': '撒母耳記下',
  '王上': '列王紀上',
  '王下': '列王紀下',
  '代上': '歷代志上',
  '代下': '歷代志下',
  '拉': '以斯拉記',
  '尼': '尼希米記',
  '斯': '以斯帖記',
  '伯': '約伯記',
  '詩': '詩篇',
  '箴': '箴言',
  '傳': '傳道書',
  '歌': '雅歌',
  '賽': '以賽亞書',
  '耶': '耶利米書',
  '哀': '耶利米哀歌',
  '結': '以西結書',
  '但': '但以理書',
  '何': '何西阿書',
  '珥': '約珥書',
  '摩': '阿摩司書',
  '俄': '俄巴底亞書',
  '拿': '約拿書',
  '彌': '彌迦書',
  '鴻': '那鴻書',
  '哈': '哈巴谷書',
  '番': '西番雅書',
  '該': '哈該書',
  '亞': '撒迦利亞書',
  '瑪': '瑪拉基書',
  '太': '馬太福音',
  '可': '馬可福音',
  '路': '路加福音',
  '約': '約翰福音',
  '徒': '使徒行傳',
  '羅': '羅馬書',
  '林前': '哥林多前書',
  '林後': '哥林多後書',
  '加': '加拉太書',
  '弗': '以弗所書',
  '腓': '腓立比書',
  '西': '歌羅西書',
  '帖前': '帖撒羅尼迦前書',
  '帖後': '帖撒羅尼迦後書',
  '提前': '提摩太前書',
  '提後': '提摩太後書',
  '多': '提多書',
  '門': '腓利門書',
  '來': '希伯來書',
  '雅': '雅各書',
  '彼前': '彼得前書',
  '彼後': '彼得後書',
  '約一': '約翰一書',
  '約二': '約翰二書',
  '約三': '約翰三書',
  '猶': '猶大書',
  '啟': '啟示錄'
};

/**
 * Get the appropriate book name mapping for a given language
 * @param {string} language - Bible language from constants
 * @returns {Object} - Book name mapping object
 */
function getBookNameMapping(language) {
  switch (language) {
    case BIBLE_LANGUAGES.ENGLISH:
      return ENGLISH_BOOK_NAMES;
    case BIBLE_LANGUAGES.SPANISH:
      return SPANISH_BOOK_NAMES;
    case BIBLE_LANGUAGES.SIMPLIFIED_CHINESE:
      return SIMPLIFIED_CHINESE_BOOK_NAMES;
    case BIBLE_LANGUAGES.TRADITIONAL_CHINESE:
      return TRADITIONAL_CHINESE_BOOK_NAMES;
    default:
      return ENGLISH_BOOK_NAMES;
  }
}

/**
 * Normalize book name input by trimming and handling case variations
 * @param {string} bookName - Raw book name from LSM API
 * @returns {string} - Normalized book name
 */
function normalizeBookName(bookName) {
  if (!bookName || typeof bookName !== 'string') {
    return '';
  }
  
  return bookName.trim();
}

/**
 * Convert any book name format to proper full name based on language
 * Handles abbreviations, lowercase variants, and already proper names
 * @param {string} bookName - Book name in any format (abbreviation, lowercase, etc.)
 * @param {string} language - Bible language from constants
 * @returns {string} - Proper full book name with correct capitalization
 */
export function getProperBookName(bookName, language = BIBLE_LANGUAGES.ENGLISH) {
  const normalized = normalizeBookName(bookName);
  if (!normalized) {
    return bookName || '';
  }
  
  const mapping = getBookNameMapping(language);
  
  // Direct mapping lookup (exact match)
  if (mapping[normalized]) {
    return mapping[normalized];
  }
  
  // Case-insensitive lookup for abbreviations
  const lowerNormalized = normalized.toLowerCase();
  for (const [abbrev, fullName] of Object.entries(mapping)) {
    if (abbrev.toLowerCase() === lowerNormalized) {
      return fullName;
    }
  }
  
  // Partial matching for common variations
  for (const [abbrev, fullName] of Object.entries(mapping)) {
    // Check if the input matches the start of an abbreviation
    if (abbrev.toLowerCase().startsWith(lowerNormalized)) {
      return fullName;
    }
    // Check if the input matches the start of a full name
    if (fullName.toLowerCase().startsWith(lowerNormalized)) {
      return fullName;
    }
  }
  
  // Fallback: capitalize first letter(s) appropriately
  return capitalizeBookName(normalized, language);
}

/**
 * Smart capitalization for book names when mapping fails
 * Handles numbered books and different language conventions
 * @param {string} bookName - Book name to capitalize
 * @param {string} language - Bible language from constants
 * @returns {string} - Capitalized book name
 */
function capitalizeBookName(bookName, language) {
  if (!bookName || typeof bookName !== 'string') {
    return bookName || '';
  }
  
  // Handle numbered books (e.g., "1 kings" -> "1 Kings")
  if (/^\d+\s+/.test(bookName)) {
    const parts = bookName.split(' ');
    return parts[0] + ' ' + parts.slice(1).map(part => 
      part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
    ).join(' ');
  }
  
  // For Chinese, return as-is since Chinese doesn't have capitalization
  if (language === BIBLE_LANGUAGES.SIMPLIFIED_CHINESE || 
      language === BIBLE_LANGUAGES.TRADITIONAL_CHINESE) {
    return bookName;
  }
  
  // Standard capitalization for other languages
  return bookName.charAt(0).toUpperCase() + bookName.slice(1).toLowerCase();
}

/**
 * Format chapter title with special handling for Psalms
 * Converts "Psalms #" to "Psalm #" format for proper grammar
 * @param {string} chapterTitle - Chapter title (e.g., "Psalms 1", "Salmos 1", "诗篇 1")
 * @param {string} language - Bible language from constants
 * @returns {string} - Formatted chapter title (e.g., "Psalm 1", "Salmo 1", "诗篇 1")
 */
export function formatChapterTitle(chapterTitle, language = BIBLE_LANGUAGES.ENGLISH) {
  if (!chapterTitle) return chapterTitle;

  const psalmsPatterns = {
    [BIBLE_LANGUAGES.ENGLISH]: /^Psalms\s+(\d+)$/i,
    [BIBLE_LANGUAGES.SPANISH]: /^Salmos\s+(\d+)$/i,
    [BIBLE_LANGUAGES.SIMPLIFIED_CHINESE]: /^诗篇\s+(\d+)$/,
    [BIBLE_LANGUAGES.TRADITIONAL_CHINESE]: /^詩篇\s+(\d+)$/
  };

  const psalmReplacements = {
    [BIBLE_LANGUAGES.ENGLISH]: 'Psalm $1',
    [BIBLE_LANGUAGES.SPANISH]: 'Salmo $1',
    [BIBLE_LANGUAGES.SIMPLIFIED_CHINESE]: '诗篇 $1',
    [BIBLE_LANGUAGES.TRADITIONAL_CHINESE]: '詩篇 $1'
  };

  const pattern = psalmsPatterns[language];
  const replacement = psalmReplacements[language];

  if (pattern && replacement && pattern.test(chapterTitle)) {
    return chapterTitle.replace(pattern, replacement);
  }

  return chapterTitle;
}

/**
 * Format a complete chapter reference with proper book name and chapter number
 * @param {string} bookName - Book name in any format
 * @param {number|string} chapter - Chapter number
 * @param {string} language - Bible language from constants
 * @returns {string} - Formatted chapter reference (e.g., "Genesis 1", "Génesis 1", "Psalm 1")
 */
export function formatChapterReference(bookName, chapter, language = BIBLE_LANGUAGES.ENGLISH) {
  const properBookName = getProperBookName(bookName, language);
  const chapterTitle = `${properBookName} ${chapter}`;
  return formatChapterTitle(chapterTitle, language);
}

/**
 * Parse and format multiple reading references
 * @param {Array} readings - Array of reading references in various formats
 * @param {string} language - Bible language from constants
 * @returns {string} - Formatted readings string (e.g., "Genesis 1, Exodus 2")
 */
export function formatReadingReferences(readings, language = BIBLE_LANGUAGES.ENGLISH) {
  if (!readings || !Array.isArray(readings)) {
    return '';
  }
  
  return readings.map(reading => {
    if (typeof reading === 'string') {
      // Handle format like "2 kings,17" or "2 kings 17" or "genesis 1"
      const parts = reading.replace(',', ' ').split(' ').filter(p => p.trim());
      if (parts.length >= 2) {
        const chapter = parts[parts.length - 1];
        const book = parts.slice(0, -1).join(' ');
        return formatChapterReference(book, chapter, language);
      }
      // Single book name without chapter
      return getProperBookName(reading, language);
    } else if (Array.isArray(reading) && reading.length >= 2) {
      // Handle [book, chapter] format
      return formatChapterReference(reading[0], reading[1], language);
    }
    return String(reading);
  }).join(', ');
}

// English book names to Chinese mappings for Chinese Firebase function responses
const ENGLISH_TO_SIMPLIFIED_CHINESE = {
  'genesis': '创世记',
  'exodus': '出埃及记',
  'leviticus': '利未记',
  'numbers': '民数记',
  'deuteronomy': '申命记',
  'joshua': '约书亚记',
  'judges': '士师记',
  'ruth': '路得记',
  '1 samuel': '撒母耳记上',
  '2 samuel': '撒母耳记下',
  '1 kings': '列王纪上',
  '2 kings': '列王纪下',
  '1 chronicles': '历代志上',
  '2 chronicles': '历代志下',
  'ezra': '以斯拉记',
  'nehemiah': '尼希米记',
  'esther': '以斯帖记',
  'job': '约伯记',
  'psalms': '诗篇',
  'proverbs': '箴言',
  'ecclesiastes': '传道书',
  'song of songs': '雅歌',
  'isaiah': '以赛亚书',
  'jeremiah': '耶利米书',
  'lamentations': '耶利米哀歌',
  'ezekiel': '以西结书',
  'daniel': '但以理书',
  'hosea': '何西阿书',
  'joel': '约珥书',
  'amos': '阿摩司书',
  'obadiah': '俄巴底亚书',
  'jonah': '约拿书',
  'micah': '弥迦书',
  'nahum': '那鸿书',
  'habakkuk': '哈巴谷书',
  'zephaniah': '西番雅书',
  'haggai': '哈该书',
  'zechariah': '撒迦利亚书',
  'malachi': '玛拉基书',
  'matthew': '马太福音',
  'mark': '马可福音',
  'luke': '路加福音',
  'john': '约翰福音',
  'acts': '使徒行传',
  'romans': '罗马书',
  '1 corinthians': '哥林多前书',
  '2 corinthians': '哥林多后书',
  'galatians': '加拉太书',
  'ephesians': '以弗所书',
  'philippians': '腓立比书',
  'colossians': '歌罗西书',
  '1 thessalonians': '帖撒罗尼迦前书',
  '2 thessalonians': '帖撒罗尼迦后书',
  '1 timothy': '提摩太前书',
  '2 timothy': '提摩太后书',
  'titus': '提多书',
  'philemon': '腓利门书',
  'hebrews': '希伯来书',
  'james': '雅各书',
  '1 peter': '彼得前书',
  '2 peter': '彼得后书',
  '1 john': '约翰一书',
  '2 john': '约翰二书',
  '3 john': '约翰三书',
  'jude': '犹大书',
  'revelation': '启示录'
};

const ENGLISH_TO_TRADITIONAL_CHINESE = {
  'genesis': '創世記',
  'exodus': '出埃及記',
  'leviticus': '利未記',
  'numbers': '民數記',
  'deuteronomy': '申命記',
  'joshua': '約書亞記',
  'judges': '士師記',
  'ruth': '路得記',
  '1 samuel': '撒母耳記上',
  '2 samuel': '撒母耳記下',
  '1 kings': '列王紀上',
  '2 kings': '列王紀下',
  '1 chronicles': '歷代志上',
  '2 chronicles': '歷代志下',
  'ezra': '以斯拉記',
  'nehemiah': '尼希米記',
  'esther': '以斯帖記',
  'job': '約伯記',
  'psalms': '詩篇',
  'proverbs': '箴言',
  'ecclesiastes': '傳道書',
  'song of songs': '雅歌',
  'isaiah': '以賽亞書',
  'jeremiah': '耶利米書',
  'lamentations': '耶利米哀歌',
  'ezekiel': '以西結書',
  'daniel': '但以理書',
  'hosea': '何西阿書',
  'joel': '約珥書',
  'amos': '阿摩司書',
  'obadiah': '俄巴底亞書',
  'jonah': '約拿書',
  'micah': '彌迦書',
  'nahum': '那鴻書',
  'habakkuk': '哈巴谷書',
  'zephaniah': '西番雅書',
  'haggai': '哈該書',
  'zechariah': '撒迦利亞書',
  'malachi': '瑪拉基書',
  'matthew': '馬太福音',
  'mark': '馬可福音',
  'luke': '路加福音',
  'john': '約翰福音',
  'acts': '使徒行傳',
  'romans': '羅馬書',
  '1 corinthians': '哥林多前書',
  '2 corinthians': '哥林多後書',
  'galatians': '加拉太書',
  'ephesians': '以弗所書',
  'philippians': '腓立比書',
  'colossians': '歌羅西書',
  '1 thessalonians': '帖撒羅尼迦前書',
  '2 thessalonians': '帖撒羅尼迦後書',
  '1 timothy': '提摩太前書',
  '2 timothy': '提摩太後書',
  'titus': '提多書',
  'philemon': '腓利門書',
  'hebrews': '希伯來書',
  'james': '雅各書',
  '1 peter': '彼得前書',
  '2 peter': '彼得後書',
  '1 john': '約翰一書',
  '2 john': '約翰二書',
  '3 john': '約翰三書',
  'jude': '猶大書',
  'revelation': '啟示錄'
};

/**
 * Convert English book name to Chinese for Chinese language Bible texts
 * This handles the case where Chinese Firebase functions return English refs
 * @param {string} englishBookName - English book name (e.g., "ruth", "1 kings")
 * @param {string} language - Chinese language variant
 * @returns {string} - Chinese book name
 */
function convertEnglishBookToChinese(englishBookName, language) {
  const normalized = englishBookName.toLowerCase().trim();
  
  if (language === BIBLE_LANGUAGES.SIMPLIFIED_CHINESE) {
    return ENGLISH_TO_SIMPLIFIED_CHINESE[normalized] || englishBookName;
  } else if (language === BIBLE_LANGUAGES.TRADITIONAL_CHINESE) {
    return ENGLISH_TO_TRADITIONAL_CHINESE[normalized] || englishBookName;
  }
  
  return englishBookName;
}

/**
 * Extract chapter title from LSM API verse reference
 * @param {string} verseRef - Verse reference like "2 Kings 16:1" or "Ap. 1:1" or "ruth 3:3"
 * @param {string} language - Bible language from constants
 * @returns {string} - Formatted chapter title (e.g., "2 Kings 16", "路得记 3", "Psalm 1")
 */
export function extractChapterTitle(verseRef, language = BIBLE_LANGUAGES.ENGLISH) {
  if (!verseRef || typeof verseRef !== 'string') {
    return '';
  }
  
  // Match patterns like "2 Kings 16:1", "Ap. 1:1", "ruth 4:1"
  const refMatch = verseRef.match(/^(.+?)\s+(\d+):(\d+)$/);
  if (refMatch) {
    let bookName = refMatch[1];
    const chapter = refMatch[2];
    
    // For Chinese languages, convert English book names to Chinese
    if (language === BIBLE_LANGUAGES.SIMPLIFIED_CHINESE || 
        language === BIBLE_LANGUAGES.TRADITIONAL_CHINESE) {
      bookName = convertEnglishBookToChinese(bookName, language);
      const chapterTitle = `${bookName} ${chapter}`;
      return formatChapterTitle(chapterTitle, language);
    }
    
    // For other languages, use normal formatting (which includes Psalms formatting)
    return formatChapterReference(bookName, chapter, language);
  }
  
  // Fallback for unusual formats
  return verseRef;
}
